import { useState } from 'react';
import { z } from 'zod';

import { RoleSelectItem } from '@apps/web/components/settings/role_select_item';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';

const inviteFormSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    role: z.enum(['admin', 'scheduler', 'restricted_scheduler']),
});

type InviteUserModalProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly organizationId: string;
    readonly onSuccess?: () => void | Promise<unknown>;
};

export function InviteUserModal({
    open,
    onOpenChange,
    organizationId,
    onSuccess,
}: InviteUserModalProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        role: 'scheduler' as 'admin' | 'scheduler' | 'restricted_scheduler',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({});

        try {
            const validatedData = inviteFormSchema.parse({
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                role: formData.role,
            });

            setIsSubmitting(true);

            await getApiClient().post({
                url: '/organizations/{organization_id}/invitations',
                pathParams: { organization_id: organizationId },
                body: {
                    email: validatedData.email,
                    first_name: validatedData.firstName,
                    last_name: validatedData.lastName,
                    role: validatedData.role,
                },
            });

            toast.success('Invitation sent successfully', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            onOpenChange(false);
            setFormData({
                email: '',
                firstName: '',
                lastName: '',
                role: 'scheduler',
            });
        } catch (error) {
            if (error instanceof z.ZodError) {
                const newErrors: Record<string, string> = {};
                error.issues.forEach((err: z.ZodIssue) => {
                    if (err.path[0]) {
                        newErrors[err.path[0].toString()] = err.message;
                    }
                });
                setErrors(newErrors);
            } else if (error instanceof Error) {
                toast.error(error.message, {
                    persistNotification: true,
                    notificationType: 'settings_update',
                });
            } else {
                toast.error('Failed to send invitation', {
                    persistNotification: true,
                    notificationType: 'settings_update',
                });
            }
            return;
        } finally {
            setIsSubmitting(false);
        }

        try {
            await onSuccess?.();
        } catch {
            toast.error('Invitation sent, but failed to refresh team members', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>Invite Team Member</DialogTitle>
                        <DialogDescription>
                            Send an invitation to join your organization.
                            They’ll receive an email with instructions to create
                            an account.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="email">
                                Email <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="colleague@example.com"
                                value={formData.email}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        email: e.target.value,
                                    })
                                }
                                className={
                                    errors['email'] ? 'border-red-500' : ''
                                }
                                required={true}
                            />
                            {errors['email'] && (
                                <p className="text-sm text-red-500">
                                    {errors['email']}
                                </p>
                            )}
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="firstName">
                                First Name{' '}
                                <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="firstName"
                                type="text"
                                placeholder="Jane"
                                value={formData.firstName}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        firstName: e.target.value,
                                    })
                                }
                                className={
                                    errors['firstName'] ? 'border-red-500' : ''
                                }
                                required={true}
                            />
                            {errors['firstName'] && (
                                <p className="text-sm text-red-500">
                                    {errors['firstName']}
                                </p>
                            )}
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="lastName">
                                Last Name{' '}
                                <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="lastName"
                                type="text"
                                placeholder="Doe"
                                value={formData.lastName}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        lastName: e.target.value,
                                    })
                                }
                                className={
                                    errors['lastName'] ? 'border-red-500' : ''
                                }
                                required={true}
                            />
                            {errors['lastName'] && (
                                <p className="text-sm text-red-500">
                                    {errors['lastName']}
                                </p>
                            )}
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="role">
                                Role <span className="text-red-500">*</span>
                            </Label>
                            <Select
                                value={formData.role}
                                onValueChange={(
                                    value:
                                        | 'admin'
                                        | 'scheduler'
                                        | 'restricted_scheduler',
                                ) => setFormData({ ...formData, role: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a role" />
                                </SelectTrigger>
                                <SelectContent>
                                    <RoleSelectItem role="scheduler" />
                                    <RoleSelectItem role="restricted_scheduler" />
                                    <RoleSelectItem role="admin" />
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Invitation'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
